import { LinkSquare02Icon } from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import type { ComponentProps } from "react"
import { Glyph, Prose } from "@motleyhand/design"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import type { Processor } from "unified"

declare module "unified" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Data {
    micromarkExtensions?: unknown[]
  }
}

/**
 * Remark plugin that disables indented code blocks (4+ leading spaces).
 * Fenced code blocks (backtick/tilde) still work normally.
 */
function remarkDisableIndentedCode(this: Processor) {
  const data = this.data()
  const micromarkExtensions =
    data.micromarkExtensions ?? (data.micromarkExtensions = [])
  micromarkExtensions.push({ disable: { null: ["codeIndented"] } })
}

function LinkBadge({ href, children }: ComponentProps<"a">) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="not-prose mr-1 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs text-muted-foreground no-underline transition-colors hover:border-foreground/20 hover:text-foreground"
    >
      <span className="max-w-40 truncate">{children}</span>
      <HugeiconsIcon
        icon={LinkSquare02Icon}
        size={12}
        className="text-muted-foreground/75"
      />
    </a>
  )
}

type Props = {
  children: string
  className?: string
  linksAsBadges?: boolean
}

export default function Markdown({
  children,
  className,
  linksAsBadges,
}: Props) {
  return (
    <Prose className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkDisableIndentedCode]}
        components={{
          h1: ({ children, node, ...props }) => (
            <h1 {...props}>
              <Glyph variant="staggered-bars" size={24} className="mr-2" />
              {children}
            </h1>
          ),
          ...(linksAsBadges && {
            a: ({ node, ...props }) => <LinkBadge {...props} />,
          }),
        }}
      >
        {children}
      </ReactMarkdown>
    </Prose>
  )
}
